// The hook every v2 surface should read through.
//
// It is a thin wrapper over `client.read` and is meant to stay thin. The dedup
// and the cache live in the client so that two components asking the same
// question share an answer even when one of them is not a React component at
// all — the appbar's health poll, say.
//
// The returned `status` is the §11.4 vocabulary from `../states`, so a surface
// can hand it straight to `<SurfaceState>` and render all five outcomes without
// writing a conditional. That is the whole ergonomic point: adopting this should
// delete code from a surface, not add it.
import { useCallback, useEffect, useRef, useState } from "react";

import { IDLE, LOADING } from "../states";
import { read } from "./client";

/**
 * Read `path` on mount, and again whenever `reload()` is called.
 *
 * Returns `{ status, data, error, httpStatus, reload }`.
 *
 * `params` is compared by value, not by identity — an object literal written
 * inline in a caller's JSX is a new object on every render, and comparing by
 * identity would re-fetch on every keystroke elsewhere on the page. This is the
 * single most common way a "cached" layer ends up making more requests than the
 * code it replaced.
 */
export default function useResource(path, { params, ttl, skip = false } = {}) {
  const [state, setState] = useState(
    skip ? { status: IDLE, data: null, error: null, httpStatus: null } : { status: LOADING, data: null, error: null, httpStatus: null }
  );

  const paramKey = params ? JSON.stringify(params) : "";
  const alive = useRef(true);

  const load = useCallback(
    async ({ fresh = false } = {}) => {
      if (skip) return;
      setState((s) => ({ ...s, status: LOADING }));
      const outcome = await read(path, {
        params,
        ttl: fresh ? 0 : ttl,
      });
      if (!alive.current) return;
      setState(outcome);
    },
    // `params` intentionally absent: `paramKey` is its value-equal stand-in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, paramKey, ttl, skip]
  );

  useEffect(() => {
    alive.current = true;
    load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  /** Re-read, bypassing the cache. What a refresh control should call. */
  const reload = useCallback(() => load({ fresh: true }), [load]);

  return { ...state, reload };
}
